/**
 * Radio input field component for Uncanny Automator
 *
 * A custom radio button component that provides a styled radio input with proper
 * accessibility features and event handling. Supports different sizes and states.
 *
 * @example
 * ```html
 * <uo-field-input-radio name="option" value="option1" size="large">
 *     <span slot="label">Option 1</span>
 * </uo-field-input-radio>
 *
 * <uo-field-input-radio name="option" value="option2" checked>
 *     <span slot="label">Option 2 (Default)</span>
 * </uo-field-input-radio>
 * ```
 *
 * @since 6.9.0 Initial implementation
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Radio input field component for Uncanny Automator
 */
@customElement( 'uo-field-input-radio' )
export class AutomatorFieldInputRadio extends LitElement {

	// Define style
	static styles = css`${unsafeCSS( styles )}`;

	/**
	 * Indicates whether the field is required
	 * @type {Boolean}
	 */
	@property( { type: Boolean } )
	accessor required = false;

	/**
	 * The value of the field, `true` if the switch is on, false otherwise.
	 * @type {Boolean}
	 */
	@property( { type: Boolean } )
	accessor checked = false;

	/**
	 * The name of the field
	 * @type {String}
	 */
	@property( { type: String } )
	accessor name = '';

	/**
	 * The value of the field
	 * @type {String}
	 */
	@property( { type: String } )
	accessor value = '';

	/**
	 * Indicates whether the field is disabled
	 * @type {Boolean}
	 */
	@property( { type: Boolean } )
	accessor disabled = false;

	/**
	 * The size of the field - 'regular' or 'large'
	 * @type {String}
	 */
	@property( { type: String, attribute: 'size' } )
	accessor size = 'regular';

	/**
	 * Renders the component's template
	 *
	 * @return {import('lit').TemplateResult} The rendered template
	 */
	render() {
		return html`
			<label class="wrapper">
				<input 
					type="radio"
					name="${this.name}"
					value="${this.value}"
					?required=${this.required}
					?disabled=${this.disabled}
					.checked=${this.checked}
					@change=${this._onChange}
				/>
				<span class="radio">
					<span class="radio-control">
						<span class="radio-control-inner"></span>
					</span>
					<span class="radio-label">
						<slot name="label"></slot>
					</span>	
				</span>
			</label>
		`;
	}

	/**
	 * Handles the input change event
	 */
	_onChange() {
		// Uncheck all other radios in the group
		const allRadios = document.querySelectorAll( 'uo-field-input-radio' );
		allRadios.forEach( radio => {
			if ( radio !== this && radio.name === this.name ) {
				radio.checked = false;
				// Also update the native input in the shadow DOM
				const input = radio.shadowRoot?.querySelector( 'input[type="radio"]' );
				if ( input ) {
					input.checked = false;
				}
			}
		} );
		// Check this one
		this.checked = true;
		// Optionally, update the native input (should already be checked)
		this.shadowRoot.querySelector( 'input[type="radio"]' ).checked = true;

		this.dispatchEvent( new CustomEvent( 'change', {
			detail: { value: this.value, checked: true },
			bubbles: true,
			composed: true
		} ) );
	}

	/**
	 * Get the input element
	 *
	 * @return {HTMLInputElement|null} The input element or null if not found
	 */
	get $input() {
		return this.shadowRoot.querySelector( 'input' );
	}
}
